import { useState, useRef, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LevelSelect from "@/components/practice/LevelSelect";
import { Send, Sparkles, Loader2, Flag } from "lucide-react";
import { toast } from "sonner";

export default function FreeTalkTab() {
  const [session, setSession] = useState(null);
  const [message, setMessage] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [level, setLevel] = useState("intermediate");
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [session?.messages]);

  const startMutation = useMutation({
    mutationFn: () => api.post("/ai/free-talk/start", null, { params: { level } }),
    onSuccess: ({ data }) => {
      setSession(data);
      setAnalysis(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Не удалось начать разговор"),
  });

  const sendMutation = useMutation({
    mutationFn: (text) => api.post(`/ai/free-talk/${session.id}/message`, { message: text }),
    onSuccess: ({ data }) => {
      setSession((s) => ({
        ...s,
        messages: [...s.messages, { role: "user", content: message }, { role: "assistant", content: data.reply }],
      }));
      setMessage("");
    },
  });

  const endMutation = useMutation({
    mutationFn: () => api.post(`/ai/free-talk/${session.id}/end`),
    onSuccess: ({ data }) => setAnalysis(data),
  });

  if (!session) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center" data-testid="freetalk-start-state">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-primary" />
        </div>
        <p className="font-medium">Свободный разговор с AI на любую тему</p>
        <LevelSelect value={level} onValueChange={setLevel} testIdPrefix="freetalk" />
        <Button onClick={() => startMutation.mutate()} disabled={startMutation.isPending} data-testid="start-freetalk-button">
          {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Начать разговор"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[60vh]" data-testid="freetalk-active-state">
      <div className="px-4 py-2 border-b border-border flex items-center justify-between">
        <p className="text-sm font-medium">Тема: {session.topic}</p>
        <Button variant="outline" size="sm" onClick={() => endMutation.mutate()} data-testid="end-freetalk-button">
          <Flag className="w-4 h-4 mr-1" /> Завершить
        </Button>
      </div>

      {analysis ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="freetalk-analysis">
          <p className="font-heading font-semibold">Обратная связь</p>
          <p className="text-sm">{analysis.feedback}</p>
          <div>
            <p className="text-xs text-emerald-600 font-semibold mb-1">Сильные стороны</p>
            <ul className="text-sm list-disc pl-5 space-y-0.5">{analysis.strengths?.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <div>
            <p className="text-xs text-orange-600 font-semibold mb-1">Над чем поработать</p>
            <ul className="text-sm list-disc pl-5 space-y-0.5">{analysis.improvements?.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
          <Button onClick={() => setSession(null)} data-testid="new-freetalk-button">Новый разговор</Button>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="freetalk-messages">
            {session.messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-xl px-3 py-2 text-sm ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.content}
                </div>
              </div>
            ))}
          </div>
          <form
            className="p-3 border-t border-border flex gap-2"
            onSubmit={(e) => { e.preventDefault(); if (message.trim()) sendMutation.mutate(message); }}
          >
            <Input value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Напишите ответ..." data-testid="freetalk-message-input" />
            <Button type="submit" disabled={sendMutation.isPending} data-testid="freetalk-send-button">
              {sendMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
